/**
 * ChannelVersionInfo - Displays current version info for a channel.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getChannelCurrentVersion } from '@/lib/api/channel-history';

interface ChannelVersionInfoProps {
  productId: string;
  channelId: string;
}

export function ChannelVersionInfo({ productId, channelId }: ChannelVersionInfoProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['channel-current-version', productId, channelId],
    queryFn: () => getChannelCurrentVersion(productId, channelId),
    staleTime: 30_000,
  });

  if (isLoading || !data?.current_version) {
    return null;
  }

  const { version_number, created_at, changed_by } = data.current_version;
  const date = new Date(created_at);
  const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <span className="font-medium text-primary-600 dark:text-primary-400">
        V{version_number}
      </span>
      <span>•</span>
      <span title={`${dateStr} ${timeStr}`}>
        {dateStr} {timeStr}
      </span>
      {changed_by && (
        <>
          <span>•</span>
          <span title={t('products.changedBy', { name: changed_by })}>
            {changed_by}
          </span>
        </>
      )}
    </>
  );
}
